import { X } from 'lucide-react'
import clsx from 'clsx'
import { useStore, selectFilteredSymbols } from '../../store/useStore'
import type { MarketType } from '../../types'
import { getSignal, SIGNAL_BG_COLOR, SIGNAL_TEXT_COLOR } from '../../utils/signal'

const MARKET_FILTERS: { id: MarketType; label: string }[] = [
  { id: 'all',    label: '全市場' },
  { id: 'crypto', label: '暗号通貨' },
  { id: 'stock',  label: '株式' },
  { id: 'fx',     label: 'FX / 外国為替' },
]

type TabId = 'dashboard' | 'analysis'

const TABS: { id: TabId; label: string }[] = [
  { id: 'dashboard',  label: 'ダッシュボード' },
  { id: 'analysis',   label: '分析' },
]

function getMarketType(symbol: string, symbols: { crypto: string[]; stock: string[]; fx: string[] } | null) {
  if (!symbols) return 'unknown'
  if (symbols.crypto.includes(symbol)) return 'crypto'
  if (symbols.stock.includes(symbol)) return 'stock'
  if (symbols.fx.includes(symbol)) return 'fx'
  return 'unknown'
}

const MARKET_BADGE_COLORS: Record<string, string> = {
  crypto: 'bg-trading-yellow/20 text-trading-yellow',
  stock: 'bg-trading-blue/20 text-trading-blue',
  fx: 'bg-trading-green/20 text-trading-green',
  unknown: 'bg-trading-muted/20 text-trading-muted',
}

interface Props {
  onClose?: () => void
}

export default function Sidebar({ onClose }: Props) {
  const {
    symbols,
    selectedSymbol,
    selectedMarket,
    selectedInterval,
    predictions,
    ensemblePredictions,
    loadingEnsemble,
    setSelectedSymbol,
    setSelectedMarket,
    loadPrediction,
    loadOHLCV,
  } = useStore()

  const filteredSymbols = useStore(selectFilteredSymbols)

  function handleSelectSymbol(symbol: string) {
    setSelectedSymbol(symbol)
    const key = `${symbol}_${selectedInterval}`
    if (!predictions[key]) loadPrediction(symbol, selectedInterval)
    if (!useStore.getState().ohlcv[key]) loadOHLCV(symbol, selectedInterval)
    onClose?.()
  }

  const { activeTab, setActiveTab } = useStore()

  return (
    <div className="flex flex-col h-full bg-trading-panel w-full">
      {/* Mobile close button */}
      {onClose && (
        <div className="flex items-center justify-between px-3 py-2 border-b border-trading-border">
          <span className="text-xs font-semibold text-trading-text">メニュー</span>
          <button
            onClick={onClose}
            className="p-1.5 rounded text-trading-muted hover:text-trading-text hover:bg-trading-border/60 transition-colors"
            aria-label="閉じる"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* ページ切替 */}
      <div className="p-3 border-b border-trading-border">
        <p className="text-[10px] text-trading-muted uppercase tracking-widest mb-2 px-1">
          ページ
        </p>
        <div className="flex flex-col gap-0.5">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => { setActiveTab(id); onClose?.() }}
              className={clsx(
                'text-left text-xs font-medium px-3 py-2 rounded transition-colors duration-150 border-l-2',
                activeTab === id
                  ? 'bg-trading-blue/20 text-trading-blue border-trading-blue pl-[10px]'
                  : 'text-trading-muted hover:text-trading-text hover:bg-trading-border/50 border-transparent pl-[10px]',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Market filter */}
      <div className="p-3 border-b border-trading-border">
        <p className="text-[10px] text-trading-muted uppercase tracking-widest mb-2 px-1">
          市場
        </p>
        <div className="flex flex-col gap-0.5">
          {MARKET_FILTERS.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setSelectedMarket(id)}
              className={clsx(
                'text-left text-xs font-medium px-3 py-2 rounded transition-colors duration-150',
                selectedMarket === id
                  ? 'bg-trading-blue/20 text-trading-blue border-l-2 border-trading-blue pl-[10px]'
                  : 'text-trading-muted hover:text-trading-text hover:bg-trading-border/50 border-l-2 border-transparent pl-[10px]',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Symbol list */}
      <div className="flex-1 overflow-y-auto p-2">
        <p className="text-[10px] text-trading-muted uppercase tracking-widest mb-2 px-1">
          シンボル（{filteredSymbols.length}）
        </p>
        <div className="flex flex-col gap-0.5">
          {filteredSymbols.map((symbol) => {
            const ensembleKey = symbol.toUpperCase()
            const ensemblePred = ensemblePredictions[ensembleKey]
            const isLoading = loadingEnsemble[ensembleKey]
            const isSelected = selectedSymbol === symbol
            const marketType = getMarketType(symbol, symbols)
            const risePct = ensemblePred?.ensemble.rise_probability

            return (
              <button
                key={symbol}
                onClick={() => handleSelectSymbol(symbol)}
                className={clsx(
                  'w-full text-left px-2 py-2 rounded transition-colors duration-150',
                  isSelected
                    ? 'bg-trading-panel border border-trading-blue/40 shadow-sm'
                    : 'hover:bg-trading-border/40 border border-transparent',
                )}
              >
                {/* Top row: symbol + badge */}
                <div className="flex items-center justify-between gap-1 mb-1">
                  <span
                    className={clsx(
                      'text-xs font-semibold font-mono truncate',
                      isSelected ? 'text-trading-text' : 'text-trading-muted',
                    )}
                  >
                    {symbol}
                  </span>
                  <span
                    className={clsx(
                      'flex-none text-[9px] font-bold uppercase px-1 py-0.5 rounded',
                      MARKET_BADGE_COLORS[marketType],
                    )}
                  >
                    {marketType === 'fx' ? 'FX' : marketType.slice(0, 2).toUpperCase()}
                  </span>
                </div>

                {/* Rise probability bar */}
                {isLoading && (
                  <div className="h-1 bg-trading-border rounded animate-pulse" />
                )}
                {!isLoading && risePct !== undefined && (
                  <div className="flex items-center gap-1.5">
                    {(() => {
                      const sig = getSignal(risePct)
                      return (
                        <>
                          <div className="flex-1 h-1 bg-trading-bg rounded overflow-hidden">
                            <div
                              className={clsx('h-full rounded transition-all duration-500', SIGNAL_BG_COLOR[sig])}
                              style={{ width: `${Math.round(risePct * 100)}%` }}
                            />
                          </div>
                          <span className={clsx('flex-none text-[9px] font-mono tabular-nums', SIGNAL_TEXT_COLOR[sig])}>
                            {Math.round(risePct * 100)}%
                          </span>
                        </>
                      )
                    })()}
                  </div>
                )}
              </button>
            )
          })}

          {filteredSymbols.length === 0 && (
            <p className="text-xs text-trading-muted text-center py-4">
              {symbols ? 'シンボルなし' : '読込中...'}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
